import { ChevronRight } from "lucide-react";
import Datatable from "@components/datatable";
import StatusChip from "@components/ui-components/status-chip";
import { formatDate } from "@helpers/date";
import DebitNotePreviewModal from "@panel/shared/debit-note/components/debit-note-preview-modal";
import useDebitNotePreview from "@panel/shared/debit-note/hooks/use-debit-note-preview";
import { DEBIT_NOTE_STATUS_LABEL, DEBIT_NOTE_STATUS_TONE } from "@src/utility/constants/panel/common";
import { formatCurrency } from "@src/utility/helpers/utils";
import useBrandRouteGuard from "../use-brand-route-guard";
import useBrandDebitNotes from "./hooks/use-brand-debit-notes";
import DebitNoteActions from "./components/debit-note-actions";
import { STATUS_CAPTION } from "./misc/constants";

const BrandDebitNote = () => {
    // Self-payout brands never have a debit note raised against them; the guard
    // bounces them back to the dashboard, the same way the nav hides the link.
    const { brandId, brand, allowed } = useBrandRouteGuard({ requiresDebitNotes: true });
    // Mirror of the disbursements page: don't fetch for a brand the guard is about to
    // bounce — switching brands rewrites :brandId without unmounting this page.
    const {
        loading, notes, pagination, fetchInvoices,
        reject, acknowledge, approve, undoAcknowledge, withdrawApproval, handlePageChange, handlePerRowsChange
    } = useBrandDebitNotes(allowed ? brandId : null);
    const preview = useDebitNotePreview({ notes, fetchInvoices });

    const columns = [
        { Header: "Debit note", accessor: "debit_note_number", Cell: ({ value }) => <span className="font-mono text-sm font-medium text-gray-900">{value}</span> },
        { Header: "Period", accessor: "payout_period", Cell: ({ value }) => <span className="text-gray-700">{value ? formatDate(value, "MMMM YYYY") : "—"}</span> },
        { Header: "Issued on", accessor: "createdAt", Cell: ({ value, row }) => <span className="text-gray-700">{formatDate(row.issued_at || value)}</span> },
        {
            Header  : "Status",
            accessor: "status",
            Cell    : ({ value }) => <StatusChip tone={DEBIT_NOTE_STATUS_TONE[value] || "neutral"} label={DEBIT_NOTE_STATUS_LABEL[value] || value} />
        },
        {
            Header  : "Amount",
            accessor: "amount",
            Cell    : ({ value, row }) => <span className="font-medium text-gray-900">{formatCurrency(value, row.currency)}</span>
        },
        {
            Header  : "",
            accessor: "_id",
            Cell    : () => (
                <span className="flex items-center justify-end gap-1 text-sm font-medium text-accent">
                    View
                    <ChevronRight className="h-3.5 w-3.5" />
                </span>
            )
        }
    ];

    return (
        <div className="p-6">
            <h1 className="text-2xl font-semibold tracking-tight">Debit notes</h1>

            <div className="mt-6 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                <Datatable
                    columns={columns}
                    data={notes}
                    loading={loading}
                    pagination={{ count: pagination.total, page: pagination.page, limit: pagination.limit }}
                    handlePageChange={handlePageChange}
                    handlePerRowsChange={handlePerRowsChange}
                    onRowClicked={preview.openPreview}
                    customNoData={<span className="text-sm text-muted">No debit notes yet.</span>} />
            </div>

            <DebitNotePreviewModal
                preview={preview}
                brand={brand}
                caption={STATUS_CAPTION[preview.previewNote?.status]}
                actions={preview.previewNote && (
                    <DebitNoteActions
                        note={preview.previewNote}
                        onReject={reject}
                        onAcknowledge={acknowledge}
                        onApprove={approve}
                        onUndoAcknowledge={undoAcknowledge}
                        onWithdrawApproval={withdrawApproval} />
                )} />
        </div>
    );
};

export default BrandDebitNote;
